import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import api from '../../config/api';
import { FaEye, FaDownload, FaFilter, FaSearch, FaSort, FaEnvelope, FaTimes, FaChevronLeft, FaChevronRight, FaToggleOn, FaToggleOff, FaUsers } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import { formatPhoneDisplay } from '../../utils/formatPhoneDisplay';
import { formatBirthDateDisplayUtc } from '../../utils/dateInput';
import { MEMBERSHIP_SEASON } from '../../config/membershipSeason';
import { isActiveMemberForSeason, formatActiveSeasonsCell } from '../../utils/memberStatus';
import './AdminPages.css';
import './AdminSubscribers.css';

/** Saisons avec filtres rapides (liste dépend de la saison). */
const QUICK_FILTER_SEASONS = new Set(['2025-2026', MEMBERSHIP_SEASON]);
const QUICK_FILTER_SEASONS_LABEL = [...QUICK_FILTER_SEASONS].sort().join(', ');
const DEFAULT_MEMBER_SEASON = '2025-2026';

/** Filtres rapides abonnés stade — BASE / FULL / STAR / STAR abo club. */
const SUBSCRIBERS_QUICK_FILTERS_SUBSCRIBERS = [
  { id: 'base', label: 'Abonné BASE' },
  { id: 'full', label: 'Abonné FULL' },
  { id: 'star', label: 'Abonné STAR' },
  { id: 'starAbo', label: 'Abonné STAR abo club' },
];

const SUBSCRIBER_QUICK_FILTER_IDS = new Set(
  SUBSCRIBERS_QUICK_FILTERS_SUBSCRIBERS.map((f) => f.id),
);

/** Filtres rapides import Excel — saison 2025-2026 uniquement. */
const SUBSCRIBERS_QUICK_FILTERS_EXCEL = [
  ...SUBSCRIBERS_QUICK_FILTERS_SUBSCRIBERS,
  { id: 'plusAbonne', label: 'Abonné membre PLUS' },
  { id: 'standardAbonne', label: 'Abonné membre STANDARD' },
  { id: 'plus', label: 'Membre PLUS' },
  { id: 'plusNoNewsletter', label: 'Membre PLUS désactivé la newsletter' },
  { id: 'standard', label: 'Membre STANDARD' },
  { id: 'plusU14NoNewsletter', label: 'Membre PLUS under 14 désactivé la newsletter' },
  { id: 'plusU14', label: 'Membre PLUS under 14' },
  { id: 'under5', label: 'Membre under 5' },
];

/** Filtres rapides cotisation JOFC — saison 2026-2027 uniquement. */
const SUBSCRIBERS_QUICK_FILTERS_JOFC = [
  { id: 'jofcAdult', label: 'JOFC Adult' },
  { id: 'jofcUnder14', label: 'JOFC under 14' },
  { id: 'jofcUnder5', label: 'JOFC Under 5' },
];

const JOFC_QUICK_FILTER_IDS = new Set(SUBSCRIBERS_QUICK_FILTERS_JOFC.map((f) => f.id));

const EXCEL_MEMBERSHIP_QUICK_FILTER_IDS = new Set(
  SUBSCRIBERS_QUICK_FILTERS_EXCEL.filter(
    (f) => !SUBSCRIBER_QUICK_FILTER_IDS.has(f.id) && !JOFC_QUICK_FILTER_IDS.has(f.id),
  ).map((f) => f.id),
);

function getQuickFiltersForSeason(season) {
  if (season === MEMBERSHIP_SEASON) {
    return [...SUBSCRIBERS_QUICK_FILTERS_SUBSCRIBERS, ...SUBSCRIBERS_QUICK_FILTERS_JOFC];
  }
  if (season === '2025-2026') return SUBSCRIBERS_QUICK_FILTERS_EXCEL;
  return [];
}

/** Anciens flags import Excel (PLUS / STANDARD / U14 / U5) — masqués en saison JOFC courante. */
function usesLegacyExcelMembershipFilters(season) {
  return season !== MEMBERSHIP_SEASON;
}

function quickFiltersSectionLabel(season) {
  if (season === MEMBERSHIP_SEASON || season === '2025-2026') return season;
  return QUICK_FILTER_SEASONS_LABEL;
}

/** L'utilisateur est rattaché à cette saison (filtre liste). Si `activeSeasons` est renseigné, il fait foi. */
function userMatchesSeasonListFilter(user, seasonParam) {
  if (!seasonParam || seasonParam === 'all') return true;
  const active = Array.isArray(user.activeSeasons) ? user.activeSeasons : [];
  if (seasonParam === 'old') {
    const noCrmSeason = user.season == null || String(user.season).trim() === '';
    return noCrmSeason && active.length === 0;
  }
  if (active.length > 0) return active.includes(seasonParam);
  if (user.season === seasonParam) return true;
  return false;
}

function userIsActiveForSeason(user, seasonParam) {
  return userMatchesSeasonListFilter(user, seasonParam);
}

const PUBLIC_SITE_URL = 'https://www.oltrefrontiera.ch';

const BULK_FROM_OPTIONS = [
  { value: 'info@oltrefrontiera.ch', label: 'info@oltrefrontiera.ch — Communication générale' },
  { value: 'ticket@oltrefrontiera.ch', label: 'ticket@oltrefrontiera.ch — Billetterie' },
  { value: 'member@oltrefrontiera.ch', label: 'member@oltrefrontiera.ch — Membres' },
];

const BULK_BATCH_SIZE = 190;
const BULK_BATCH_THRESHOLD = 200;

const EMAIL_QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ color: [] }, { background: [] }],
    [{ align: [] }],
    ['link', 'image'],
    ['clean'],
  ],
};

const EMAIL_QUILL_FORMATS = [
  'header',
  'bold', 'italic', 'underline', 'strike',
  'list', 'bullet',
  'color', 'background',
  'align',
  'link', 'image',
];

function plainTextToEditorHtml(text) {
  const raw = String(text || '').trim();
  if (!raw) return '';
  if (/<[a-z][\s\S]*>/i.test(raw)) return raw;
  return raw
    .split('\n')
    .map((line) => `<p>${line || '<br>'}</p>`)
    .join('');
}

function isEditorContentEmpty(html) {
  const text = String(html || '')
    .replace(/<style[\s\S]*?<\/style>/gi, '')
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return text.length === 0;
}

function computeBatchPlan(recipientCount) {
  if (recipientCount <= BULK_BATCH_THRESHOLD) {
    return { batched: false, batchCount: 1, batchSize: recipientCount };
  }
  const batchCount = Math.ceil(recipientCount / BULK_BATCH_SIZE);
  return { batched: true, batchCount, batchSize: BULK_BATCH_SIZE };
}

const AdminSubscribers = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const tableContainerRef = useRef(null);
  const topScrollbarRef = useRef(null);
  const [selectedSubscriber, setSelectedSubscriber] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedParent, setSelectedParent] = useState(null);
  const [showParentModal, setShowParentModal] = useState(false);
  const [selectedSeason, setSelectedSeason] = useState('all'); // Season filter
  const [filterMember, setFilterMember] = useState('all');
  const [filterPayment, setFilterPayment] = useState('all');
  const [filterLanguage, setFilterLanguage] = useState('all');
  const [filterJuveCard, setFilterJuveCard] = useState('all');
  const [filterSubscription, setFilterSubscription] = useState('all');
  const [filterMinors, setFilterMinors] = useState('all');
  const [filterSecteur, setFilterSecteur] = useState('');
  const [filterRang, setFilterRang] = useState('');
  const [filterPlace, setFilterPlace] = useState('');
  const [filterStar, setFilterStar] = useState('all');
  const [filterBase, setFilterBase] = useState('all');
  const [filterPlus, setFilterPlus] = useState('all');
  const [filterPlusU14, setFilterPlusU14] = useState('all');
  const [filterStandard, setFilterStandard] = useState('all');
  const [filterAbonnes, setFilterAbonnes] = useState('all');
  const [filterNonAbonne, setFilterNonAbonne] = useState('all');
  const [filterNewsletter, setFilterNewsletter] = useState('all');
  const [filterUnder5, setFilterUnder5] = useState('all');
  const [filterJOFCFull, setFilterJOFCFull] = useState('all');
  const [filterStarAbonne, setFilterStarAbonne] = useState('all');
  const [filterStandardAbonne, setFilterStandardAbonne] = useState('all');
  const [filterPlusAbonne, setFilterPlusAbonne] = useState('all');
  const [filterJofcAdult, setFilterJofcAdult] = useState('all');
  const [filterJofcUnder14, setFilterJofcUnder14] = useState('all');
  const [filterJofcUnder5, setFilterJofcUnder5] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeQuickFilter, setActiveQuickFilter] = useState(null);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailData, setEmailData] = useState({
    subject: '',
    message: '',
    template: 'custom',
    fromEmail: 'info@oltrefrontiera.ch',
  });
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [groupCreating, setGroupCreating] = useState(false);
  const [emailSendError, setEmailSendError] = useState('');
  const [apiTemplates, setApiTemplates] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 50;
  const [memberStatusUpdatingId, setMemberStatusUpdatingId] = useState(null);
  const [filterInactiveFor2026, setFilterInactiveFor2026] = useState(false);

  const memberActiveFilterLabel =
    selectedSeason === 'all' || selectedSeason === 'old'
      ? 'Membre actif'
      : `Membre actif ${selectedSeason}`;

  const visibleQuickFilters = useMemo(
    () => getQuickFiltersForSeason(selectedSeason),
    [selectedSeason],
  );

  const showLegacyExcelMembershipFilters = usesLegacyExcelMembershipFilters(selectedSeason);

  useEffect(() => {
    fetchSubscribers();
    fetchEmailTemplates();
  }, [selectedSeason, filterInactiveFor2026]);

  useEffect(() => {
    if (activeQuickFilter && !visibleQuickFilters.some((f) => f.id === activeQuickFilter)) {
      setActiveQuickFilter(null);
    }
  }, [selectedSeason, activeQuickFilter, visibleQuickFilters]);

  useEffect(() => {
    if (showLegacyExcelMembershipFilters) return;
    setFilterStandard('all');
    setFilterStandardAbonne('all');
    setFilterPlus('all');
    setFilterPlusAbonne('all');
    setFilterPlusU14('all');
    setFilterUnder5('all');
  }, [showLegacyExcelMembershipFilters]);

  const fetchEmailTemplates = async () => {
    try {
      const response = await api.get('/email/templates');
      setApiTemplates(response.data);
    } catch (error) {
      console.error('Error fetching email templates:', error);
    }
  };

  const fetchSubscribers = async () => {
    try {
      setLoading(true);
      const params = filterInactiveFor2026
        ? { season: 'all' }
        : selectedSeason !== 'all'
          ? { season: selectedSeason }
          : {};
      const response = await api.get('/users', { params, timeout: 120000 });
      const data = response.data;
      if (!Array.isArray(data)) {
        console.error('GET /users: expected an array, received:', typeof data);
      }
      setSubscribers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching subscribers:', error);
      alert('Erreur lors du chargement des inscrits');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to display yes/x instead of true/false
  const formatBoolean = (value) => {
    if (value === true || value === 'true' || value === 'yes' || value === 'Yes' || value === 'Si' || value === 'Oui') {
      return '✓';
    }
    return '✗';
  };

  const handleViewDetails = (subscriber) => {
    setSelectedSubscriber(subscriber);
    setShowDetailModal(true);
  };

  const handleToggleMemberStatus = async (subscriber) => {
    const nextIsMember = !subscriber.isMember;
    if (
      !nextIsMember &&
      !window.confirm(
        `Rendre inactif le membre « ${subscriber.firstName} ${subscriber.lastName} » ?`
      )
    ) {
      return;
    }

    try {
      setMemberStatusUpdatingId(subscriber._id);
      const { data } = await api.put(`/users/${subscriber._id}`, { isMember: nextIsMember });
      const updated = data.user || {};
      const patchFromUpdate = {
        isMember: typeof updated.isMember === 'boolean' ? updated.isMember : nextIsMember,
        ...(updated.membershipDate !== undefined ? { membershipDate: updated.membershipDate } : {}),
        ...(updated.accountActivated !== undefined
          ? { accountActivated: updated.accountActivated }
          : {}),
        ...(updated.membershipFlags ? { membershipFlags: updated.membershipFlags } : {}),
        ...(updated.membershipFlagsBySeason
          ? { membershipFlagsBySeason: updated.membershipFlagsBySeason }
          : {}),
      };
      setSubscribers((prev) =>
        prev.map((s) =>
          String(s._id) === String(subscriber._id) ? { ...s, ...patchFromUpdate } : s
        )
      );
      setSelectedSubscriber((prev) =>
        prev && String(prev._id) === String(subscriber._id)
          ? { ...prev, ...patchFromUpdate }
          : prev
      );
    } catch (error) {
      console.error('Error updating member status:', error);
      const msg =
        error.response?.data?.error ||
        error.response?.data?.message ||
        'Erreur lors de la mise à jour du statut membre';
      alert(msg);
    } finally {
      setMemberStatusUpdatingId(null);
    }
  };

  const handleViewParent = (subscriber) => {
    // Check if parent is a registered user or just embedded info
    if (subscriber.parentUser) {
      // Parent is a registered user
      setSelectedParent({
        ...subscriber.parentUser,
        isRegisteredUser: true
      });
    } else if (subscriber.parentInfo) {
      // Parent info is only embedded data
      setSelectedParent({
        ...subscriber.parentInfo,
        isRegisteredUser: false
      });
    }
    setShowParentModal(true);
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  // Count active filters
  const activeFiltersCount = [
    filterMember !== 'all',
    filterPayment !== 'all',
    filterLanguage !== 'all',
    filterJuveCard !== 'all',
    filterSubscription !== 'all',
    filterMinors !== 'all',
    filterSecteur !== '',
    filterRang !== '',
    filterPlace !== '',
    filterStar !== 'all',
    filterBase !== 'all',
    showLegacyExcelMembershipFilters && filterPlus !== 'all',
    showLegacyExcelMembershipFilters && filterPlusU14 !== 'all',
    showLegacyExcelMembershipFilters && filterStandard !== 'all',
    filterAbonnes !== 'all',
    filterNonAbonne !== 'all',
    filterNewsletter !== 'all',
    showLegacyExcelMembershipFilters && filterUnder5 !== 'all',
    filterJOFCFull !== 'all',
    filterStarAbonne !== 'all',
    showLegacyExcelMembershipFilters && filterStandardAbonne !== 'all',
    showLegacyExcelMembershipFilters && filterPlusAbonne !== 'all',
    filterJofcAdult !== 'all',
    filterJofcUnder14 !== 'all',
    filterJofcUnder5 !== 'all',
    filterInactiveFor2026,
    searchTerm !== ''
  ].filter(Boolean).length;

  // Email templates
  const emailTemplates = {
    custom: { subject: '', message: '' },
    ticketAlert: {
      subject: 'Nouveaux billets disponibles pour le match de la Juventus',
      message: `Chers membres,

Nous avons le plaisir de vous informer que de nouveaux billets sont disponibles pour le prochain match de la Juventus.

Pour réserver vos places, connectez-vous à votre espace membre : ${PUBLIC_SITE_URL}/billets

Nombre de places limitées - Réservez vite !

Forza Juve!
L'équipe Oltre Frontiera`
    },
    newsletter: {
      subject: 'Newsletter Oltre Frontiera',
      message: `Chers membres,

Voici les dernières nouvelles de notre fan club :

[Ajoutez votre contenu ici]

Restez connectés pour plus d'actualités !

Forza Juve!
L'équipe Oltre Frontiera`
    },
    reminder: {
      subject: 'Rappel - Mise à jour de votre profil',
      message: `Cher membre,

Nous vous rappelons qu'il est important de mettre à jour votre profil membre avec vos informations actuelles.

Connectez-vous sur : ${PUBLIC_SITE_URL}/login

Si vous avez oublié votre mot de passe, veuillez utiliser la fonctionnalité de réinitialisation de mot de passe ou contactez l'administration.

Cordialement,
L'équipe Oltre Frontiera`
    },
    payment: {
      subject: 'Rappel - Paiement de la cotisation',
      message: `Cher membre,

Nous vous rappelons que le paiement de votre cotisation annuelle est en attente.

Pour régulariser votre situation, veuillez vous connecter à votre espace membre.

Merci de votre compréhension.

Cordialement,
L'équipe Oltre Frontiera`
    }
  };

  // Selection handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedMembers(filteredAndSortedSubscribers.map(s => s._id));
    } else {
      setSelectedMembers([]);
    }
  };

  const handleSelectMember = (memberId) => {
    setSelectedMembers(prev => 
      prev.includes(memberId)
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };

  const handleTemplateChange = (template) => {
    // Check if it's an API template (starts with 'api_')
    if (template.startsWith('api_')) {
      const templateId = template.replace('api_', '');
      const apiTemplate = apiTemplates.find(t => t._id === templateId);
      if (apiTemplate) {
        setEmailData({
          ...emailData,
          template,
          subject: apiTemplate.subject,
          message: plainTextToEditorHtml(apiTemplate.htmlContent || apiTemplate.textContent || ''),
        });
      }
    } else if (emailTemplates[template]) {
      // Hardcoded template
      setEmailData({
        ...emailData,
        template,
        subject: emailTemplates[template].subject,
        message: plainTextToEditorHtml(emailTemplates[template].message),
      });
    }
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    
    if (selectedMembers.length === 0) {
      alert('Veuillez sélectionner au moins un membre');
      return;
    }

    if (!emailData.subject || isEditorContentEmpty(emailData.message)) {
      alert('Veuillez remplir le sujet et le message');
      return;
    }

    const batchPlan = computeBatchPlan(selectedMembers.length);
    if (batchPlan.batched) {
      const confirmMsg =
        `${selectedMembers.length} destinataires sélectionnés.\n\n` +
        `L'envoi sera réparti en ${batchPlan.batchCount} lot(s) de ${batchPlan.batchSize} maximum, ` +
        `espacés de 24 h, pour respecter la limite d'emails sortants.\n\n` +
        `Confirmer la planification ?`;
      if (!window.confirm(confirmMsg)) {
        return;
      }
    }

    const sendId = typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `bulk-${Date.now()}-${Math.random().toString(36).slice(2)}`;

    try {
      setEmailSending(true);
      setEmailSendError('');

      const recipients = subscribers.filter(s => selectedMembers.includes(s._id));
      
      const emailPayload = {
        sendId,
        recipients: recipients.map(r => ({
          email: r.email,
          firstName: r.firstName,
          lastName: r.lastName
        })),
        subject: emailData.subject,
        message: emailData.message,
        fromEmail: emailData.fromEmail,
      };

      const response = await api.post('/email/bulk-send', emailPayload, { timeout: 600000 });
      
      let message = `✅ ${response.data.message || `Campagne planifiée pour ${response.data.sent} membre(s).`}\n\n`;
      message += `La diffusion sera traitée par Infomaniak Newsletter (meilleure délivrabilité et file d'attente).`;

      if (response.data.fromEmail) {
        message += `\n\nExpéditeur : ${response.data.fromEmail}`;
      }

      if (response.data.batched) {
        message += `\n\n📦 Envoi en ${response.data.batchCount} lot(s) de ${response.data.batchSize} max.`;
        if (response.data.scheduledAt) {
          message += `\nPremier lot : ${new Date(response.data.scheduledAt).toLocaleString('fr-FR')}`;
        }
        if (response.data.scheduledUntil) {
          message += `\nDernier lot : ${new Date(response.data.scheduledUntil).toLocaleString('fr-FR')}`;
        }
      } else if (response.data.scheduledAt) {
        message += `\nEnvoi prévu : ${new Date(response.data.scheduledAt).toLocaleString('fr-FR')}`;
      }

      if (response.data.groupName || response.data.groupId) {
        message += `\n\nGroupe Infomaniak : ${response.data.groupName || 'admin-bulk'}`;
        if (response.data.groupId) {
          message += ` (#${response.data.groupId})`;
        }
        if (response.data.groupSubscriberCount) {
          message += `\n${response.data.groupSubscriberCount} destinataire(s) dans le groupe.`;
        }
      }

      if (response.data.campaignId && !response.data.batched) {
        message += `\n\nRéférence campagne : #${response.data.campaignId}`;
      }

      if (response.data.batched && Array.isArray(response.data.batches)) {
        message += `\n\nCampagnes planifiées :`;
        response.data.batches.forEach((batch) => {
          message += `\n  • Lot ${batch.batchIndex}/${batch.batchTotal} : ${batch.sent} destinataire(s)`;
          if (batch.scheduledAt) {
            message += ` — ${new Date(batch.scheduledAt).toLocaleString('fr-FR')}`;
          }
          if (batch.campaignId) {
            message += ` (#${batch.campaignId})`;
          }
        });
      }
      
      if (response.data.skipped > 0) {
        message += `\n\n⚠️ ${response.data.skipped} membre(s) ignoré(s) (emails générés @oltrefrontiera.import ou invalides)`;
      }
      
      alert(message);
      
      setShowEmailModal(false);
      setShowEmailPreview(false);
      setSelectedMembers([]);
      setEmailData({
        subject: '',
        message: '',
        template: 'custom',
        fromEmail: 'info@oltrefrontiera.ch',
      });
      
    } catch (error) {
      console.error('Error sending emails:', error);
      const errorMsg =
        error.response?.data?.error ||
        error.response?.data?.message ||
        'Erreur lors de l\'envoi de la campagne Infomaniak';
      setEmailSendError(errorMsg);
      alert(errorMsg);
    } finally {
      setEmailSending(false);
    }
  };

  const getSelectedMembersData = () => {
    return subscribers.filter(s => selectedMembers.includes(s._id));
  };

  const handleCreateGroup = async () => {
    if (selectedMembers.length === 0) {
      alert('Veuillez sélectionner au moins un membre');
      return;
    }

    const defaultName = `groupe-${new Date().toISOString().slice(0, 10)}`;
    const groupName = window.prompt(
      'Nom du groupe Infomaniak :',
      defaultName
    );

    if (groupName === null) {
      return;
    }

    const trimmedName = groupName.trim();
    if (!trimmedName) {
      alert('Le nom du groupe est requis');
      return;
    }

    const sendId = typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `group-${Date.now()}-${Math.random().toString(36).slice(2)}`;

    try {
      setGroupCreating(true);

      const recipients = getSelectedMembersData();

      const response = await api.post('/email/create-group', {
        sendId,
        groupName: trimmedName,
        recipients: recipients.map((r) => ({
          email: r.email,
          firstName: r.firstName,
          lastName: r.lastName,
        })),
      }, { timeout: 180000 });

      let message = `✅ Groupe Infomaniak créé : ${response.data.groupName}`;
      if (response.data.groupId) {
        message += ` (#${response.data.groupId})`;
      }
      message += `\n\n${response.data.groupSubscriberCount ?? response.data.subscriberCount} destinataire(s) importé(s).`;

      if (response.data.skipped > 0) {
        message += `\n\n⚠️ ${response.data.skipped} membre(s) ignoré(s) (emails générés @oltrefrontiera.import ou invalides)`;
      }

      alert(message);
      setSelectedMembers([]);
    } catch (error) {
      console.error('Error creating Infomaniak group:', error);
      const errorMsg =
        error.response?.data?.error ||
        error.response?.data?.message ||
        'Erreur lors de la création du groupe Infomaniak';
      alert(errorMsg);
    } finally {
      setGroupCreating(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('fr-FR');
  };

  // Helper function to determine country from phone number
  const getCountryFromPhone = (phone) => {
    if (!phone) return null;

    const phoneStr = String(phone).trim();
    const normalized = phoneStr.startsWith('+') ? phoneStr : `+${phoneStr}`;

    if (normalized.startsWith('+33')) {
      return 'France';
    } else if (normalized.startsWith('+41')) {
      return 'Suisse';
    } else if (normalized.startsWith('+39')) {
      return 'Italie';
    }

    return null;
  };

  // Get country for display (from address or phone)
  const getDisplayCountry = (subscriber) => {
    if (subscriber.address?.country) {
      return subscriber.address.country;
    }
    
    // Fallback: determine from phone number
    const countryFromPhone = getCountryFromPhone(subscriber.phone);
    return countryFromPhone || 'N/A';
  };

  const exportToExcel = () => {
    const isJofcExport = selectedSeason === MEMBERSHIP_SEASON;
    const activeMemberLabel =
      selectedSeason === 'all' || selectedSeason === 'old'
        ? 'Membre actif'
        : `Membre actif ${selectedSeason}`;

    const headersBeforeMembership = [
      'Date activation', 'Civilité', 'Nom', 'Prénom', 'Date de naissance',
      'Pays de naissance', 'Canton/Province/Département de naissance', 'Ville de naissance',
      'Document', 'Numéro de document', 'Validité du document',
      "Date d'émission du document", "Canton/Département d'émission", "Commune/localité d'émission",
      'Sexe',
      'Adresse de domicile', 'Code Postal', 'Ville de domicile', 'Canton/Province/Département', 'Pays de domicile',
      'Mobile', 'Email',
    ];

    const membershipHeaders = isJofcExport
      ? ['JOFC Adult', 'JOFC under 14', 'JOFC Under 5', 'Newsletter']
      : [
          'Membre STANDARD', 'Membre Abonné STANDARD', 'Membre PLUS', 'Membre Abonné PLUS',
          'Membre UNDER14', 'Membre UNDER5', 'Newsletter',
        ];

    const headersAfterMembership = [
      'En possession de la Juventus Card', 'Numéro Juventus Card', 'No Webticket', 'Validité Juventus Card',
      'Abonné au stade', 'Abonné BASE', 'Abonné FULL', 'Abonné STAR', 'Abonné STAR abo du club',
      'Tribune', 'Secteur', 'Rangée', 'Place',
      'En possession de la carte Juventus Premium Member/J1897', 'Numéro carte B & W - J1897', 'Validité carte B & W - J1897',
      'Codice Fiscale', 'Marketing', 'Profilazione', 'Autorisation mineur', 'Privacy',
      'Nom & Prénom du parent',
      'Langue par defaut',
    ];

    const headers = [...headersBeforeMembership, activeMemberLabel, 'Saisons actives', ...membershipHeaders, ...headersAfterMembership];

    const formatActiveMember = (sub) => {
      if (selectedSeason === 'all' || selectedSeason === 'old') {
        return sub.isMember ? 'Oui' : 'Non';
      }
      return isActiveMemberForSeason(sub, selectedSeason) ? 'Oui' : 'Non';
    };

    const membershipCells = (sub) =>
      isJofcExport
        ? [
            sub.membershipFlags?.jofcAdult ? 'Oui' : 'Non',
            sub.membershipFlags?.jofcUnder14 ? 'Oui' : 'Non',
            sub.membershipFlags?.jofcUnder5 ? 'Oui' : 'Non',
            sub.membershipFlags?.newsletter ? 'Oui' : 'Non',
          ]
        : [
            sub.membershipFlags?.standard ? 'Oui' : 'Non',
            sub.membershipFlags?.standardAbonne ? 'Oui' : 'Non',
            sub.membershipFlags?.plus ? 'Oui' : 'Non',
            sub.membershipFlags?.plusAbonne ? 'Oui' : 'Non',
            sub.membershipFlags?.plusU14 ? 'Oui' : 'Non',
            sub.membershipFlags?.under5 ? 'Oui' : 'Non',
            sub.membershipFlags?.newsletter ? 'Oui' : 'Non',
          ];

    const identityTail = (sub) => [
      sub.juventusFanCard?.has ? 'Oui' : 'Non',
      sub.juventusFanCard?.number || '',
      sub.juventusFanCard?.webticketPIN || '',
      formatDate(sub.juventusFanCard?.expiryDate),
      sub.subscription?.has ? 'Oui' : 'Non',
      sub.membershipFlags?.base ? 'Oui' : 'Non',
      sub.membershipFlags?.jofcAbonnesFull ? 'Oui' : 'Non',
      sub.membershipFlags?.star ? 'Oui' : 'Non',
      sub.membershipFlags?.starAboClub ? 'Oui' : 'Non',
      sub.subscription?.tribune || '',
      sub.subscription?.settore || '',
      sub.subscription?.fila || '',
      sub.subscription?.posto || '',
      sub.blackWhiteCard?.has ? 'Oui' : 'Non',
      sub.blackWhiteCard?.number || '',
      formatDate(sub.blackWhiteCard?.expiryDate),
      sub.codiceFiscale || '',
      sub.consents?.marketing ? 'Oui' : 'Non',
      sub.consents?.profiling ? 'Oui' : 'Non',
      sub.consents?.imageRights ? 'Oui' : 'Non',
      sub.consents?.termsAccepted ? 'Oui' : 'Non',
      sub.parentUser
        ? `${sub.parentUser.firstName} ${sub.parentUser.lastName}`.trim()
        : sub.parentInfo
          ? `${sub.parentInfo?.firstName || ''} ${sub.parentInfo?.lastName || ''}`.trim() || ''
          : '',
      sub.preferredLanguage === 'it' ? 'Italien' : 'Français',
    ];
    
    const excelData = [...filteredAndSortedSubscribers]
      .sort((a, b) => {
        const byLastName = (a.lastName || '').localeCompare(b.lastName || '', 'fr', {
          sensitivity: 'base',
        });
        if (byLastName !== 0) return byLastName;
        return (a.firstName || '').localeCompare(b.firstName || '', 'fr', { sensitivity: 'base' });
      })
      .map((sub) => {
      const addressStr = sub.address?.street && sub.address?.streetNumber 
        ? `${sub.address.street} ${sub.address.streetNumber}`.trim()
        : sub.address?.street || '';
      
      return [
        formatDate(sub.membershipDate),
        sub.civilite || '',
        sub.lastName || '',
        sub.firstName || '',
        formatBirthDateDisplayUtc(sub.dateOfBirth, 'fr-CH') || 'N/A',
        sub.birthPlace?.country || '',
        sub.birthPlace?.canton || '',
        sub.birthPlace?.city || '',
        sub.identity?.type || '',
        sub.identity?.number || '',
        formatDate(sub.identity?.expiryDate),
        formatDate(sub.identity?.issueDate),
        sub.identity?.issueCanton || '',
        sub.identity?.issueCity || '',
        sub.sexe === 'Homme' ? 'Masculin' : sub.sexe === 'Femme' ? 'Féminin' : sub.sexe || '',
        addressStr,
        sub.address?.postalCode || '',
        sub.address?.city || '',
        sub.address?.canton || '',
        sub.address?.country || '',
        sub.phone != null && String(sub.phone).trim() !== ''
          ? formatPhoneDisplay(sub.phone)
          : '',
        sub.email || '',
        formatActiveMember(sub),
        formatActiveSeasonsCell(sub),
        ...membershipCells(sub),
        ...identityTail(sub),
      ];
    });

    // Create worksheet
    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...excelData]);
    
    // Set column widths for better readability
    const columnWidths = headers.map((_, index) => {
      // Calculate max width based on header and data
      const headerLength = headers[index].length;
      const maxDataLength = Math.max(
        ...excelData.map(row => String(row[index] || '').length)
      );
      return { wch: Math.max(headerLength, maxDataLength, 10) };
    });
    worksheet['!cols'] = columnWidths;

    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Membres');

    // Generate Excel file
    const seasonLabel = selectedSeason === 'all' ? 'all-seasons' : 
                       selectedSeason === 'old' ? 'old-data' : 
                       selectedSeason.replace(/\s+/g, '-');
    const fileName = `subscribers_${seasonLabel}_${new Date().toISOString().split('T')[0]}.xlsx`;
    
    XLSX.writeFile(workbook, fileName);
  };

  // Filter and sort subscribers - using useMemo for performance
  const filteredAndSortedSubscribers = useMemo(() => {
    return subscribers
    .filter(sub => {
      // Enhanced search - searches multiple fields
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = 
        sub.firstName?.toLowerCase().includes(searchLower) ||
        sub.lastName?.toLowerCase().includes(searchLower) ||
        sub.email?.toLowerCase().includes(searchLower) ||
        sub.phone?.toLowerCase().includes(searchLower) ||
        sub.address?.city?.toLowerCase().includes(searchLower) ||
        sub.address?.canton?.toLowerCase().includes(searchLower) ||
        sub.address?.postalCode?.toLowerCase().includes(searchLower) ||
        sub.birthPlace?.city?.toLowerCase().includes(searchLower) ||
        sub.juventusFanCard?.number?.toLowerCase().includes(searchLower);

      const matchesMember =
        filterMember === 'all'
          ? true
          : filterMember === 'member'
            ? selectedSeason === 'all' || selectedSeason === 'old'
              ? sub.isMember
              : isActiveMemberForSeason(sub, selectedSeason)
            : selectedSeason === 'all' || selectedSeason === 'old'
              ? !sub.isMember
              : !isActiveMemberForSeason(sub, selectedSeason);

      const matchesPayment = 
        filterPayment === 'all' ? true :
        sub.payment?.status === filterPayment;

      const matchesLanguage = 
        filterLanguage === 'all' ? true :
        sub.preferredLanguage === filterLanguage;

      const matchesJuveCard = 
        filterJuveCard === 'all' ? true :
        filterJuveCard === 'yes' ? sub.juventusFanCard?.has :
        !sub.juventusFanCard?.has;

      const matchesSubscription = 
        filterSubscription === 'all' ? true :
        filterSubscription === 'yes' ? sub.subscription?.has :
        !sub.subscription?.has;

      const matchesMinors = 
        filterMinors === 'all' ? true :
        filterMinors === 'yes' ? sub.isMinor :
        !sub.isMinor;

      const matchesSecteur = 
        filterSecteur === '' ? true :
        sub.subscription?.settore?.toLowerCase().includes(filterSecteur.toLowerCase());

      const matchesRang = 
        filterRang === '' ? true :
        sub.subscription?.fila?.toLowerCase().includes(filterRang.toLowerCase());

      const matchesPlace = 
        filterPlace === '' ? true :
        sub.subscription?.posto?.toLowerCase().includes(filterPlace.toLowerCase());

      const matchesStar =
        filterStar === 'all'
          ? true
          : filterStar === 'yes'
            ? sub.membershipFlags?.star
            : !sub.membershipFlags?.star;

      const matchesBase =
        filterBase === 'all'
          ? true
          : filterBase === 'yes'
            ? sub.membershipFlags?.base
            : !sub.membershipFlags?.base;

      const matchesPlus =
        filterPlus === 'all'
          ? true
          : filterPlus === 'yes'
            ? sub.membershipFlags?.plus
            : !sub.membershipFlags?.plus;

      const matchesPlusU14 =
        filterPlusU14 === 'all'
          ? true
          : filterPlusU14 === 'yes'
            ? sub.membershipFlags?.plusU14
            : !sub.membershipFlags?.plusU14;

      const matchesStandard =
        filterStandard === 'all'
          ? true
          : filterStandard === 'yes'
            ? sub.membershipFlags?.standard
            : !sub.membershipFlags?.standard;

      const matchesAbonnes =
        filterAbonnes === 'all'
          ? true
          : filterAbonnes === 'yes'
            ? sub.membershipFlags?.jofcAbonnesFull
            : !sub.membershipFlags?.jofcAbonnesFull;

      const matchesJofcAdult =
        filterJofcAdult === 'all'
          ? true
          : filterJofcAdult === 'yes'
            ? sub.membershipFlags?.jofcAdult
            : !sub.membershipFlags?.jofcAdult;

      const matchesJofcUnder14 =
        filterJofcUnder14 === 'all'
          ? true
          : filterJofcUnder14 === 'yes'
            ? sub.membershipFlags?.jofcUnder14
            : !sub.membershipFlags?.jofcUnder14;

      const matchesJofcUnder5 =
        filterJofcUnder5 === 'all'
          ? true
          : filterJofcUnder5 === 'yes'
            ? sub.membershipFlags?.jofcUnder5
            : !sub.membershipFlags?.jofcUnder5;

      const matchesNonAbonne = 
        filterNonAbonne === 'all' ? true :
        filterNonAbonne === 'yes' ? sub.membershipFlags?.nonAbonne :
        !sub.membershipFlags?.nonAbonne;

      const matchesNewsletter = 
        filterNewsletter === 'all' ? true :
        filterNewsletter === 'yes' ? sub.membershipFlags?.newsletter :
        !sub.membershipFlags?.newsletter;

      const matchesUnder5 =
        filterUnder5 === 'all'
          ? true
          : filterUnder5 === 'yes'
            ? sub.membershipFlags?.under5
            : !sub.membershipFlags?.under5;

      const matchesJOFCFull = 
        filterJOFCFull === 'all' ? true :
        filterJOFCFull === 'yes' ? sub.membershipFlags?.jofcAbonnesFull :
        !sub.membershipFlags?.jofcAbonnesFull;

      const matchesStarAbonne =
        filterStarAbonne === 'all'
          ? true
          : filterStarAbonne === 'yes'
            ? sub.membershipFlags?.starAboClub
            : !sub.membershipFlags?.starAboClub;

      const matchesStandardAbonne = 
        filterStandardAbonne === 'all' ? true :
        filterStandardAbonne === 'yes' ? sub.membershipFlags?.standardAbonne :
        !sub.membershipFlags?.standardAbonne;

      const matchesPlusAbonne = 
        filterPlusAbonne === 'all' ? true :
        filterPlusAbonne === 'yes' ? sub.membershipFlags?.plusAbonne :
        !sub.membershipFlags?.plusAbonne;

      const matchesInactiveFor2026 = filterInactiveFor2026
        ? !userIsActiveForSeason(sub, MEMBERSHIP_SEASON) &&
          (selectedSeason === 'all' || userMatchesSeasonListFilter(sub, selectedSeason))
        : true;

      return matchesInactiveFor2026 && matchesSearch && matchesMember && matchesPayment && 
             matchesLanguage && matchesJuveCard && matchesSubscription && matchesMinors &&
             matchesSecteur && matchesRang && matchesPlace &&
             matchesStar && matchesBase && matchesPlus && matchesPlusU14 && matchesStandard &&
             matchesAbonnes && matchesNonAbonne && matchesNewsletter &&
             matchesUnder5 && matchesJOFCFull &&
             matchesStarAbonne && matchesStandardAbonne && matchesPlusAbonne &&
             matchesJofcAdult && matchesJofcUnder14 && matchesJofcUnder5;
    })
    .sort((a, b) => {
      let compareA, compareB;

      switch(sortBy) {
        case 'name':
          compareA = `${a.lastName} ${a.firstName}`.toLowerCase();
          compareB = `${b.lastName} ${b.firstName}`.toLowerCase();
          break;
        case 'email':
          compareA = a.email?.toLowerCase() || '';
          compareB = b.email?.toLowerCase() || '';
          break;
        case 'city':
          compareA = a.address?.city?.toLowerCase() || '';
          compareB = b.address?.city?.toLowerCase() || '';
          break;
        case 'createdAt':
          compareA = new Date(a.createdAt);
          compareB = new Date(b.createdAt);
          break;
        case 'membershipDate':
          compareA = a.membershipDate ? new Date(a.membershipDate) : new Date(0);
          compareB = b.membershipDate ? new Date(b.membershipDate) : new Date(0);
          break;
        case 'dateOfBirth':
          compareA = a.dateOfBirth ? new Date(a.dateOfBirth) : new Date(0);
          compareB = b.dateOfBirth ? new Date(b.dateOfBirth) : new Date(0);
          break;
        case 'amount':
          compareA = a.payment?.amount || 0;
          compareB = b.payment?.amount || 0;
          break;
        case 'documentExpiry':
          compareA = a.identity?.expiryDate ? new Date(a.identity.expiryDate) : new Date(0);
          compareB = b.identity?.expiryDate ? new Date(b.identity.expiryDate) : new Date(0);
          break;
        case 'star':
          compareA = a.membershipFlags?.star ? 1 : 0;
          compareB = b.membershipFlags?.star ? 1 : 0;
          break;
        case 'base':
          compareA = a.membershipFlags?.base ? 1 : 0;
          compareB = b.membershipFlags?.base ? 1 : 0;
          break;
        case 'plus':
          compareA = a.membershipFlags?.plus ? 1 : 0;
          compareB = b.membershipFlags?.plus ? 1 : 0;
          break;
        case 'plusU14':
          compareA = a.membershipFlags?.plusU14 ? 1 : 0;
          compareB = b.membershipFlags?.plusU14 ? 1 : 0;
          break;
        case 'standard':
          compareA = a.membershipFlags?.standard ? 1 : 0;
          compareB = b.membershipFlags?.standard ? 1 : 0;
          break;
        case 'abonnes':
          compareA = a.membershipFlags?.jofcAbonnesFull ? 1 : 0;
          compareB = b.membershipFlags?.jofcAbonnesFull ? 1 : 0;
          break;
        case 'nonAbonne':
          compareA = a.membershipFlags?.nonAbonne ? 1 : 0;
          compareB = b.membershipFlags?.nonAbonne ? 1 : 0;
          break;
        case 'newsletter':
          compareA = a.membershipFlags?.newsletter ? 1 : 0;
          compareB = b.membershipFlags?.newsletter ? 1 : 0;
          break;
        case 'under5':
          compareA = a.membershipFlags?.under5 ? 1 : 0;
          compareB = b.membershipFlags?.under5 ? 1 : 0;
          break;
        default:
          return 0;
      }

      if (compareA < compareB) return sortOrder === 'asc' ? -1 : 1;
      if (compareA > compareB) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [subscribers, searchTerm, selectedSeason, filterInactiveFor2026, filterMember, filterPayment, filterLanguage, filterJuveCard, filterSubscription, filterMinors, filterSecteur, filterRang, filterPlace, filterStar, filterBase, filterPlus, filterPlusU14, filterStandard, filterAbonnes, filterNonAbonne, filterNewsletter, filterUnder5, filterJOFCFull, filterStarAbonne, filterStandardAbonne, filterPlusAbonne, filterJofcAdult, filterJofcUnder14, filterJofcUnder5, sortBy, sortOrder]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredAndSortedSubscribers.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedSubscribers = filteredAndSortedSubscribers.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedSeason, filterInactiveFor2026, filterMember, filterPayment, filterLanguage, filterJuveCard, filterSubscription, filterMinors, filterSecteur, filterRang, filterPlace, filterStar, filterBase, filterPlus, filterPlusU14, filterStandard, filterAbonnes, filterNonAbonne, filterNewsletter, filterUnder5, filterJOFCFull, filterStarAbonne, filterStandardAbonne, filterPlusAbonne, filterJofcAdult, filterJofcUnder14, filterJofcUnder5, sortBy, sortOrder]);

  // Quick filter for current season groups
  const applyGroupFilter = (groupKey) => {
    setActiveQuickFilter(groupKey);
    const targetsCurrentSeason =
      JOFC_QUICK_FILTER_IDS.has(groupKey) || SUBSCRIBER_QUICK_FILTER_IDS.has(groupKey);
    const targetsMembershipSeason =
      targetsCurrentSeason || EXCEL_MEMBERSHIP_QUICK_FILTER_IDS.has(groupKey);
    setSelectedSeason((prev) => {
      if (prev === 'all' || prev === 'old') {
        return targetsMembershipSeason ? MEMBERSHIP_SEASON : '2025-2026';
      }
      if (targetsMembershipSeason && prev === MEMBERSHIP_SEASON) return MEMBERSHIP_SEASON;
      if (!targetsMembershipSeason && prev === '2025-2026') return '2025-2026';
      if (targetsMembershipSeason) return MEMBERSHIP_SEASON;
      return '2025-2026';
    });
    setSearchTerm('');
    setFilterMember(targetsCurrentSeason ? 'all' : 'member');
    setFilterPayment('all');
    setFilterLanguage('all');
    setFilterJuveCard('all');
    setFilterSubscription('all');
    setFilterMinors('all');
    setFilterSecteur('');
    setFilterRang('');
    setFilterPlace('');
    setFilterStar('all');
    setFilterBase('all');
    setFilterPlus('all');
    setFilterPlusU14('all');
    setFilterStandard('all');
    setFilterAbonnes('all');
    setFilterNonAbonne('all');
    setFilterNewsletter('all');
    setFilterUnder5('all');
    setFilterJOFCFull('all');
    setFilterStarAbonne('all');
    setFilterStandardAbonne('all');
    setFilterPlusAbonne('all');
    setFilterJofcAdult('all');
    setFilterJofcUnder14('all');
    setFilterJofcUnder5('all');

    switch (groupKey) {
      case 'base': setFilterBase('yes'); break;
      case 'full': setFilterAbonnes('yes'); break;
      case 'star': setFilterStar('yes'); break;
      case 'starAbo': setFilterStarAbonne('yes'); break;
      case 'plusAbonne': setFilterPlusAbonne('yes'); break;
      case 'standardAbonne': setFilterStandardAbonne('yes'); break;
      case 'plus': setFilterPlus('yes'); break;
      case 'plusNoNewsletter': setFilterPlus('yes'); setFilterNewsletter('no'); break;
      case 'standard': setFilterStandard('yes'); break;
      case 'plusU14NoNewsletter': setFilterPlusU14('yes'); setFilterNewsletter('no'); break;
      case 'plusU14': setFilterPlusU14('yes'); break;
      case 'under5': setFilterUnder5('yes'); break;
      case 'jofcAdult': setFilterJofcAdult('yes'); break;
      case 'jofcUnder14': setFilterJofcUnder14('yes'); break;
      case 'jofcUnder5': setFilterJofcUnder5('yes'); break;
      default: break;
    }
  };

  const goToPreviousPage = () => {
    setCurrentPage(prev => Math.max(1, prev - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goToNextPage = () => {
    setCurrentPage(prev => Math.min(totalPages, prev + 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle scroll indicators and sync top scrollbar
  useEffect(() => {
    const container = tableContainerRef.current;
    const topScrollbar = topScrollbarRef.current;
    if (!container || !topScrollbar) return;

    // Update top scrollbar content width
    const updateTopScrollbarWidth = () => {
      const content = topScrollbar.querySelector('.scrollbar-top-content');
      const table = container.querySelector('table');
      if (content && table) {
        const tableWidth = table.scrollWidth;
        content.style.width = `${tableWidth}px`;
      }
    };

    // Sync main container scroll to top scrollbar
    const syncToTopScrollbar = () => {
      topScrollbar.scrollLeft = container.scrollLeft;
    };

    // Sync top scrollbar scroll to main container
    const syncFromTopScrollbar = () => {
      container.scrollLeft = topScrollbar.scrollLeft;
    };

    // Handle scroll indicators
    const handleScroll = () => {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      if (scrollLeft > 0) {
        container.classList.add('scroll-left');
      } else {
        container.classList.remove('scroll-left');
      }
      if (scrollLeft < scrollWidth - clientWidth - 1) {
        container.classList.add('scroll-right');
      } else {
        container.classList.remove('scroll-right');
      }
      
      // Sync to top scrollbar
      syncToTopScrollbar();
    };

    // Set up event listeners
    container.addEventListener('scroll', handleScroll);
    topScrollbar.addEventListener('scroll', syncFromTopScrollbar);
    
    // Update scrollbar width and sync after DOM update
    const updateTimeout = setTimeout(() => {
      updateTopScrollbarWidth();
      handleScroll();
    }, 100);

    // Also update on window resize
    const handleResize = () => {
      updateTopScrollbarWidth();
      syncToTopScrollbar();
    };
    window.addEventListener('resize', handleResize);

    return () => {
      container.removeEventListener('scroll', handleScroll);
      topScrollbar.removeEventListener('scroll', syncFromTopScrollbar);
      window.removeEventListener('resize', handleResize);
      clearTimeout(updateTimeout);
    };
  }, [filteredAndSortedSubscribers]);

  return (
    <div className="admin-page">
      <div className="admin-container">
        <div className="admin-page-header">
          <h1>Inscrits / Membres</h1>
          <div className="header-actions">
            <div className="season-selector" style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <label htmlFor="season-select">Saison:</label>
                <select
                  id="season-select"
                  value={selectedSeason}
                  onChange={(e) => setSelectedSeason(e.target.value)}
                  style={{ padding: '8px', fontSize: '14px' }}
                >
                  <option value="all">Toutes les saisons</option>
                  <option value="old">Anciennes données</option>
                  <option value="2025-2026">2025-2026</option>
                  <option value="2026-2027">2026-2027</option>
                </select>
              </div>
              <label
                htmlFor="inactive-2026-filter-subscribers"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  fontSize: '14px',
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
              >
                <input
                  id="inactive-2026-filter-subscribers"
                  type="checkbox"
                  checked={filterInactiveFor2026}
                  onChange={(e) => setFilterInactiveFor2026(e.target.checked)}
                />
                Non actif {MEMBERSHIP_SEASON}
              </label>
            </div>
            {selectedMembers.length > 0 && (
              <>
                <button
                  className="btn-create-group"
                  onClick={handleCreateGroup}
                  disabled={groupCreating || emailSending}
                >
                  <FaUsers /> {groupCreating ? 'Création…' : `Créer un groupe (${selectedMembers.length})`}
                </button>
                <button 
                  className="btn-email" 
                  onClick={() => {
                    setEmailSendError('');
                    setShowEmailPreview(false);
                    setShowEmailModal(true);
                  }}
                  disabled={groupCreating || emailSending}
                >
                  <FaEnvelope /> Envoyer email ({selectedMembers.length})
                </button>
              </>
            )}
            <button 
              className="btn-clear" 
              onClick={() => {
                setSearchTerm('');
                setFilterMember('all');
                setFilterPayment('all');
                setFilterLanguage('all');
                setFilterJuveCard('all');
                setFilterSubscription('all');
                setFilterMinors('all');
                setFilterSecteur('');
                setFilterRang('');
                setFilterPlace('');
                setFilterStar('all');
                setFilterBase('all');
                setFilterPlus('all');
                setFilterPlusU14('all');
                setFilterStandard('all');
                setFilterAbonnes('all');
                setFilterNonAbonne('all');
                setFilterNewsletter('all');
                setFilterUnder5('all');
                setFilterJOFCFull('all');
                setFilterStarAbonne('all');
                setFilterStandardAbonne('all');
                setFilterPlusAbonne('all');
                setFilterJofcAdult('all');
                setFilterJofcUnder14('all');
                setFilterJofcUnder5('all');
                setFilterInactiveFor2026(false);
                setActiveQuickFilter(null);
                setSortBy('createdAt');
                setSortOrder('desc');
                setSelectedMembers([]);
              }}
            >
              Réinitialiser filtres
            </button>
            <button className="btn-export" onClick={exportToExcel}>
              <FaDownload /> Exporter Excel ({filteredAndSortedSubscribers.length})
            </button>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="filters-section">
          <div className="search-header">
            <h3>Recherche et filtres {activeFiltersCount > 0 && <span className="filter-count">({activeFiltersCount} actifs)</span>}</h3>
          </div>
          
          {visibleQuickFilters.length > 0 && (
            <div className="quick-group-filters">
              <span className="quick-filters-label">
                Filtres rapides (saison {quickFiltersSectionLabel(selectedSeason)}) :
              </span>
              <div className="quick-filter-buttons">
                {visibleQuickFilters.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    className={`quick-filter-btn ${activeQuickFilter === f.id ? 'active' : ''}`}
                    onClick={() => applyGroupFilter(f.id)}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          
          <div className="search-box">
            <FaSearch />
            <input
              type="text"
              placeholder="Rechercher par nom, email, téléphone, ville, code postal, n° carte Juventus..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button 
                className="clear-search" 
                onClick={() => setSearchTerm('')}
                title="Effacer la recherche"
              >
                ✕
              </button>
            )}
          </div>
          
          {/* Filters organized by column order */}
          <div className="filters">
            <div className="filter-group">
              <label>
                <FaFilter />
                {memberActiveFilterLabel}:
              </label>
              <select value={filterMember} onChange={(e) => setFilterMember(e.target.value)}>
                <option value="all">Tous</option>
                <option value="member">Membres actifs</option>
                <option value="non-member">Non-membres</option>
              </select>
            </div>

            {showLegacyExcelMembershipFilters && (
              <>
                <div className="filter-group">
                  <label>Membre STANDARD:</label>
                  <select value={filterStandard} onChange={(e) => setFilterStandard(e.target.value)}>
                    <option value="all">Tous</option>
                    <option value="yes">Oui</option>
                    <option value="no">Non</option>
                  </select>
                </div>

                <div className="filter-group">
                  <label>Membre Abonné STANDARD:</label>
                  <select
                    value={filterStandardAbonne}
                    onChange={(e) => setFilterStandardAbonne(e.target.value)}
                  >
                    <option value="all">Tous</option>
                    <option value="yes">Oui</option>
                    <option value="no">Non</option>
                  </select>
                </div>

                <div className="filter-group">
                  <label>Membre PLUS:</label>
                  <select value={filterPlus} onChange={(e) => setFilterPlus(e.target.value)}>
                    <option value="all">Tous</option>
                    <option value="yes">Oui</option>
                    <option value="no">Non</option>
                  </select>
                </div>

                <div className="filter-group">
                  <label>Membre Abonné PLUS:</label>
                  <select value={filterPlusAbonne} onChange={(e) => setFilterPlusAbonne(e.target.value)}>
                    <option value="all">Tous</option>
                    <option value="yes">Oui</option>
                    <option value="no">Non</option>
                  </select>
                </div>
              </>
            )}

            <div className="filter-group">
              <label>JOFC Adult:</label>
              <select value={filterJofcAdult} onChange={(e) => setFilterJofcAdult(e.target.value)}>
                <option value="all">Tous</option>
                <option value="yes">Oui</option>
                <option value="no">Non</option>
              </select>
            </div>

            <div className="filter-group">
              <label>JOFC under 14:</label>
              <select value={filterJofcUnder14} onChange={(e) => setFilterJofcUnder14(e.target.value)}>
                <option value="all">Tous</option>
                <option value="yes">Oui</option>
                <option value="no">Non</option>
              </select>
            </div>

            <div className="filter-group">
              <label>JOFC Under 5:</label>
              <select value={filterJofcUnder5} onChange={(e) => setFilterJofcUnder5(e.target.value)}>
                <option value="all">Tous</option>
                <option value="yes">Oui</option>
                <option value="no">Non</option>
              </select>
            </div>

            {showLegacyExcelMembershipFilters && (
              <>
                <div className="filter-group">
                  <label>Membre UNDER14:</label>
                  <select value={filterPlusU14} onChange={(e) => setFilterPlusU14(e.target.value)}>
                    <option value="all">Tous</option>
                    <option value="yes">Oui</option>
                    <option value="no">Non</option>
                  </select>
                </div>

                <div className="filter-group">
                  <label>Membre UNDER5:</label>
                  <select value={filterUnder5} onChange={(e) => setFilterUnder5(e.target.value)}>
                    <option value="all">Tous</option>
                    <option value="yes">Oui</option>
                    <option value="no">Non</option>
                  </select>
                </div>
              </>
            )}

            <div className="filter-group">
              <label>Newsletter:</label>
              <select value={filterNewsletter} onChange={(e) => setFilterNewsletter(e.target.value)}>
                <option value="all">Tous</option>
                <option value="yes">Oui</option>
                <option value="no">Non</option>
              </select>
            </div>

            <div className="filter-group">
              <label>En possession de la Juventus Card:</label>
              <select value={filterJuveCard} onChange={(e) => setFilterJuveCard(e.target.value)}>
                <option value="all">Tous</option>
                <option value="yes">Avec carte</option>
                <option value="no">Sans carte</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Abonné au stade:</label>
              <select value={filterSubscription} onChange={(e) => setFilterSubscription(e.target.value)}>
                <option value="all">Tous</option>
                <option value="yes">Avec abonnement</option>
                <option value="no">Sans abonnement</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Abonné BASE:</label>
              <select value={filterBase} onChange={(e) => setFilterBase(e.target.value)}>
                <option value="all">Tous</option>
                <option value="yes">Oui</option>
                <option value="no">Non</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Abonné FULL:</label>
              <select value={filterAbonnes} onChange={(e) => setFilterAbonnes(e.target.value)}>
                <option value="all">Tous</option>
                <option value="yes">Oui</option>
                <option value="no">Non</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Abonné STAR:</label>
              <select value={filterStar} onChange={(e) => setFilterStar(e.target.value)}>
                <option value="all">Tous</option>
                <option value="yes">Oui</option>
                <option value="no">Non</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Abonné STAR abo club:</label>
              <select value={filterStarAbonne} onChange={(e) => setFilterStarAbonne(e.target.value)}>
                <option value="all">Tous</option>
                <option value="yes">Oui</option>
                <option value="no">Non</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Non abonné:</label>
              <select value={filterNonAbonne} onChange={(e) => setFilterNonAbonne(e.target.value)}>
                <option value="all">Tous</option>
                <option value="yes">Oui</option>
                <option value="no">Non</option>
              </select>
            </div>
          </div>

          {/* Subscription location filters */}
          <div className="filters">
            <div className="filter-group">
              <label>Secteur:</label>
              <input
                type="text"
                value={filterSecteur}
                onChange={(e) => setFilterSecteur(e.target.value)}
                placeholder="Filtrer par secteur..."
                style={{ padding: '0.5rem 1rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.95rem' }}
              />
            </div>

            <div className="filter-group">
              <label>Rangée:</label>
              <input
                type="text"
                value={filterRang}
                onChange={(e) => setFilterRang(e.target.value)}
                placeholder="Filtrer par rangée..."
                style={{ padding: '0.5rem 1rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.95rem' }}
              />
            </div>

            <div className="filter-group">
              <label>Place:</label>
              <input
                type="text"
                value={filterPlace}
                onChange={(e) => setFilterPlace(e.target.value)}
                placeholder="Filtrer par place..."
                style={{ padding: '0.5rem 1rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.95rem' }}
              />
            </div>

            <div className="filter-group">
              <label>Mineurs:</label>
              <select value={filterMinors} onChange={(e) => setFilterMinors(e.target.value)}>
                <option value="all">Tous</option>
                <option value="yes">Mineurs uniquement</option>
                <option value="no">Majeurs uniquement</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Langue:</label>
              <select value={filterLanguage} onChange={(e) => setFilterLanguage(e.target.value)}>
                <option value="all">Toutes</option>
                <option value="fr">🇫🇷 Français</option>
                <option value="it">🇮🇹 Italien</option>
              </select>
            </div>
          </div>

          <div className="sort-controls">
            <div className="filter-group">
              <label>Trier par:</label>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="name">Nom</option>
                <option value="email">Email</option>
                <option value="dateOfBirth">Date de Naissance</option>
                <option value="membershipDate">Date d'adhésion</option>
                <option value="amount">Montant</option>
                <option value="documentExpiry">Validité du document</option>
                <option value="star">Star</option>
                <option value="base">Base</option>
                <option value="plus">Plus</option>
                <option value="plusU14">Plus U14</option>
                <option value="standard">Standard</option>
                <option value="abonnes">Abonné FULL</option>
                <option value="nonAbonne">Non abonné</option>
                <option value="newsletter">Newsletter</option>
                <option value="under5">Under 5</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Ordre:</label>
              <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}>
                <option value="desc">Décroissant</option>
                <option value="asc">Croissant</option>
              </select>
            </div>
          </div>
        </div>

        <div className="stats-bar">
          <div className="stat-item">
            <strong>{filteredAndSortedSubscribers.length}</strong>
            <span>Résultats filtrés</span>
          </div>
          <div className="stat-item">
            <strong>{subscribers.length}</strong>
            <span>Total inscrits</span>
          </div>
          <div className="stat-item">
            <strong>{subscribers.filter(s => s.isMember).length}</strong>
            <span>Membres actifs</span>
          </div>
          <div className="stat-item">
            <strong>{subscribers.filter(s => s.payment?.status === 'completed').length}</strong>
            <span>Paiements validés</span>
          </div>
          <div className="stat-item">
            <strong>{subscribers.filter(s => s.juventusFanCard?.has).length}</strong>
            <span>Carte Juventus</span>
          </div>
        </div>

        {loading ? (
          <div className="loading">Chargement...</div>
        ) : (
          <>
            {selectedMembers.length > 0 && (
              <div className="selection-bar">
                <span>
                  <strong>{selectedMembers.length}</strong> membre(s) sélectionné(s)
                </span>
                <div className="selection-actions">
                  <button
                    className="btn-select-action btn-create-group"
                    onClick={handleCreateGroup}
                    disabled={groupCreating || emailSending}
                  >
                    <FaUsers /> {groupCreating ? 'Création…' : 'Créer un groupe'}
                  </button>
                  <button 
                    className="btn-select-action"
                    onClick={() => {
                      setEmailSendError('');
                      setShowEmailPreview(false);
                      setShowEmailModal(true);
                    }}
                    disabled={groupCreating || emailSending}
                  >
                    <FaEnvelope /> Envoyer email
                  </button>
                  <button 
                    className="btn-select-action btn-deselect"
                    onClick={() => setSelectedMembers([])}
                  >
                    <FaTimes /> Désélectionner tout
                  </button>
                </div>
              </div>
            )}
            
            {/* Top scrollbar */}
            <div className="scrollbar-top-wrapper">
              <div 
                className="scrollbar-top"
                ref={topScrollbarRef}
              >
                <div className="scrollbar-top-content" />
              </div>
            </div>
            
            <div 
              className="admin-table-container" 
              ref={tableContainerRef}
            >
            <table className="admin-table subscribers-table">
              <thead>
                <tr>
                  <th className="checkbox-col">
                    <input
                      type="checkbox"
                      checked={selectedMembers.length === filteredAndSortedSubscribers.length && filteredAndSortedSubscribers.length > 0}
                      onChange={handleSelectAll}
                      title="Sélectionner tout"
                    />
                  </th>
                  <th className="sortable" onClick={() => handleSort('membershipDate')}>
                    Date activation {sortBy === 'membershipDate' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th>Civilité</th>
                  <th className="sortable" onClick={() => handleSort('name')}>
                    Nom {sortBy === 'name' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th>Prénom</th>
                  <th className="sortable" onClick={() => handleSort('dateOfBirth')}>
                    Date de naissance {sortBy === 'dateOfBirth' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th>Pays de naissance</th>
                  <th>Canton/Province/Département de naissance</th>
                  <th>Ville de naissance</th>
                  <th>Document</th>
                  <th>Numéro de document</th>
                  <th className="sortable" onClick={() => handleSort('documentExpiry')}>
                    Validité du document {sortBy === 'documentExpiry' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th>Date d&apos;émission</th>
                  <th>Canton/Département d&apos;émission</th>
                  <th>Commune/localité d&apos;émission</th>
                  <th>Sexe</th>
                  <th>Adresse de domicile</th>
                  <th>Code Postal</th>
                  <th>Ville de domicile</th>
                  <th>Canton/Province/Département</th>
                  <th>Pays de domicile</th>
                  <th>Mobile</th>
                  <th className="sortable" onClick={() => handleSort('email')}>
                    Email {sortBy === 'email' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th>{memberActiveFilterLabel}</th>
                  <th>Saisons actives</th>
                  <th className="sortable" onClick={() => handleSort('standard')}>
                    Membre STANDARD {sortBy === 'standard' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th>Membre Abonné STANDARD</th>
                  <th className="sortable" onClick={() => handleSort('plus')}>
                    Membre PLUS {sortBy === 'plus' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th>Membre Abonné PLUS</th>
                  <th className="sortable" onClick={() => handleSort('plusU14')}>
                    Membre UNDER14 {sortBy === 'plusU14' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="sortable" onClick={() => handleSort('under5')}>
                    Membre UNDER5 {sortBy === 'under5' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="sortable" onClick={() => handleSort('newsletter')}>
                    Newsletter {sortBy === 'newsletter' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th>En possession de la Juventus Card</th>
                  <th>Numéro Juventus Card</th>
                  <th>No Webticket</th>
                  <th>Validité Juventus Card</th>
                  <th>Abonné au stade</th>
                  <th className="sortable" onClick={() => handleSort('base')}>
                    Abonné BASE {sortBy === 'base' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="sortable" onClick={() => handleSort('abonnes')}>
                    Abonné FULL {sortBy === 'abonnes' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="sortable" onClick={() => handleSort('star')}>
                    Abonné STAR {sortBy === 'star' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th>Abonné STAR abo du club</th>
                  <th>Tribune</th>
                  <th>Secteur</th>
                  <th>Rangée</th>
                  <th>Place</th>
                  <th>En possession de la carte Juventus Premium Member/J1897</th>
                  <th>Numéro carte B & W - J1897</th>
                  <th>Validité carte B & W - J1897</th>
                  <th>Codice Fiscale</th>
                  <th>Marketing</th>
                  <th>Profilazione</th>
                  <th>Autorisation mineur</th>
                  <th>Privacy</th>
                  <th>Nom & Prénom du parent</th>
                  <th>Langue par defaut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedSubscribers.map((subscriber) => {
                  const addressStr = subscriber.address?.street && subscriber.address?.streetNumber 
                    ? `${subscriber.address.street} ${subscriber.address.streetNumber}`.trim()
                    : subscriber.address?.street || '';
                  
                  return (
                    <tr key={subscriber._id} className={selectedMembers.includes(subscriber._id) ? 'selected' : ''}>
                      <td className="checkbox-col">
                        <input
                          type="checkbox"
                          checked={selectedMembers.includes(subscriber._id)}
                          onChange={() => handleSelectMember(subscriber._id)}
                        />
                      </td>
                      <td>{subscriber.membershipDate ? formatDate(subscriber.membershipDate) : '—'}</td>
                      <td>{subscriber.civilite || '—'}</td>
                      <td>{subscriber.lastName || '—'}</td>
                      <td>{subscriber.firstName || '—'}</td>
                      <td>
                        {subscriber.dateOfBirth
                          ? formatBirthDateDisplayUtc(subscriber.dateOfBirth, 'fr-CH') || '—'
                          : '—'}
                      </td>
                      <td>{subscriber.birthPlace?.country || '—'}</td>
                      <td>{subscriber.birthPlace?.canton || '—'}</td>
                      <td>{subscriber.birthPlace?.city || '—'}</td>
                      <td>{subscriber.identity?.type || '—'}</td>
                      <td>{subscriber.identity?.number || '—'}</td>
                      <td>{subscriber.identity?.expiryDate ? formatDate(subscriber.identity.expiryDate) : '—'}</td>
                      <td>{subscriber.identity?.issueDate ? formatDate(subscriber.identity.issueDate) : '—'}</td>
                      <td>{subscriber.identity?.issueCanton || '—'}</td>
                      <td>{subscriber.identity?.issueCity || '—'}</td>
                      <td>{subscriber.sexe === 'Homme' ? 'Masculin' : subscriber.sexe === 'Femme' ? 'Féminin' : subscriber.sexe || '—'}</td>
                      <td>{addressStr || '—'}</td>
                      <td>{subscriber.address?.postalCode || '—'}</td>
                      <td>{subscriber.address?.city || '—'}</td>
                      <td>{subscriber.address?.canton || '—'}</td>
                      <td>{subscriber.address?.country || '—'}</td>
                      <td>{formatPhoneDisplay(subscriber.phone)}</td>
                      <td>{subscriber.email || '—'}</td>
                      <td>
                        {(() => {
                          const isActive =
                            selectedSeason === 'all' || selectedSeason === 'old'
                              ? subscriber.isMember
                              : isActiveMemberForSeason(subscriber, selectedSeason);
                          return (
                            <span className={`badge ${isActive ? 'badge-success' : 'badge-warning'}`}>
                              {isActive ? 'Oui' : 'Non'}
                            </span>
                          );
                        })()}
                      </td>
                      <td style={{ maxWidth: '200px', whiteSpace: 'normal' }} title={formatActiveSeasonsCell(subscriber)}>
                        {formatActiveSeasonsCell(subscriber)}
                      </td>
                      <td className="boolean-col" style={{ color: subscriber.membershipFlags?.standard ? '#27ae60' : '#e74c3c' }}>
                        {formatBoolean(subscriber.membershipFlags?.standard)}
                      </td>
                      <td className="boolean-col" style={{ color: subscriber.membershipFlags?.standardAbonne ? '#27ae60' : '#e74c3c' }}>
                        {formatBoolean(subscriber.membershipFlags?.standardAbonne)}
                      </td>
                      <td className="boolean-col" style={{ color: subscriber.membershipFlags?.plus ? '#27ae60' : '#e74c3c' }}>
                        {formatBoolean(subscriber.membershipFlags?.plus)}
                      </td>
                      <td className="boolean-col" style={{ color: subscriber.membershipFlags?.plusAbonne ? '#27ae60' : '#e74c3c' }}>
                        {formatBoolean(subscriber.membershipFlags?.plusAbonne)}
                      </td>
                      <td className="boolean-col" style={{ color: subscriber.membershipFlags?.plusU14 ? '#27ae60' : '#e74c3c' }}>
                        {formatBoolean(subscriber.membershipFlags?.plusU14)}
                      </td>
                      <td className="boolean-col" style={{ color: subscriber.membershipFlags?.under5 ? '#27ae60' : '#e74c3c' }}>
                        {formatBoolean(subscriber.membershipFlags?.under5)}
                      </td>
                      <td className="boolean-col" style={{ color: subscriber.membershipFlags?.newsletter ? '#27ae60' : '#e74c3c' }}>
                        {formatBoolean(subscriber.membershipFlags?.newsletter)}
                      </td>
                      <td className="boolean-col" style={{ color: subscriber.juventusFanCard?.has ? '#27ae60' : '#e74c3c' }}>
                        {formatBoolean(subscriber.juventusFanCard?.has)}
                      </td>
                      <td>{subscriber.juventusFanCard?.number || '—'}</td>
                      <td>{subscriber.juventusFanCard?.webticketPIN || '—'}</td>
                      <td>{subscriber.juventusFanCard?.expiryDate ? formatDate(subscriber.juventusFanCard.expiryDate) : '—'}</td>
                      <td className="boolean-col" style={{ color: subscriber.subscription?.has ? '#27ae60' : '#e74c3c' }}>
                        {formatBoolean(subscriber.subscription?.has)}
                      </td>
                      <td className="boolean-col" style={{ color: subscriber.membershipFlags?.base ? '#27ae60' : '#e74c3c' }}>
                        {formatBoolean(subscriber.membershipFlags?.base)}
                      </td>
                      <td className="boolean-col" style={{ color: subscriber.membershipFlags?.jofcAbonnesFull ? '#27ae60' : '#e74c3c' }}>
                        {formatBoolean(subscriber.membershipFlags?.jofcAbonnesFull)}
                      </td>
                      <td className="boolean-col" style={{ color: subscriber.membershipFlags?.star ? '#27ae60' : '#e74c3c' }}>
                        {formatBoolean(subscriber.membershipFlags?.star)}
                      </td>
                      <td className="boolean-col" style={{ color: subscriber.membershipFlags?.starAboClub ? '#27ae60' : '#e74c3c' }}>
                        {formatBoolean(subscriber.membershipFlags?.starAboClub)}
                      </td>
                      <td>{subscriber.subscription?.tribune || '—'}</td>
                      <td>{subscriber.subscription?.settore || '—'}</td>
                      <td>{subscriber.subscription?.fila || '—'}</td>
                      <td>{subscriber.subscription?.posto || '—'}</td>
                      <td className="boolean-col" style={{ color: subscriber.blackWhiteCard?.has ? '#27ae60' : '#e74c3c' }}>
                        {formatBoolean(subscriber.blackWhiteCard?.has)}
                      </td>
                      <td>{subscriber.blackWhiteCard?.number || '—'}</td>
                      <td>{subscriber.blackWhiteCard?.expiryDate ? formatDate(subscriber.blackWhiteCard.expiryDate) : '—'}</td>
                      <td>{subscriber.codiceFiscale || '—'}</td>
                      <td className="boolean-col" style={{ color: subscriber.consents?.marketing ? '#27ae60' : '#e74c3c' }}>
                        {formatBoolean(subscriber.consents?.marketing)}
                      </td>
                      <td className="boolean-col" style={{ color: subscriber.consents?.profiling ? '#27ae60' : '#e74c3c' }}>
                        {formatBoolean(subscriber.consents?.profiling)}
                      </td>
                      <td className="boolean-col" style={{ color: subscriber.consents?.imageRights ? '#27ae60' : '#e74c3c' }}>
                        {formatBoolean(subscriber.consents?.imageRights)}
                      </td>
                      <td className="boolean-col" style={{ color: subscriber.consents?.termsAccepted ? '#27ae60' : '#e74c3c' }}>
                        {formatBoolean(subscriber.consents?.termsAccepted)}
                      </td>
                      <td>
                        {subscriber.isMinor && (subscriber.parentUser || subscriber.parentInfo) ? (
                          <button
                            className="parent-link"
                            onClick={() => handleViewParent(subscriber)}
                            title="Voir les informations du parent"
                          >
                            {subscriber.parentUser 
                              ? `${subscriber.parentUser.firstName} ${subscriber.parentUser.lastName}`
                              : `${subscriber.parentInfo?.firstName || ''} ${subscriber.parentInfo?.lastName || ''}`.trim() || '—'
                            }
                            {subscriber.parentUser && (
                              <span className="parent-badge" title="Parent enregistré dans le système">✓</span>
                            )}
                          </button>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td>{subscriber.preferredLanguage === 'it' ? 'Italien' : 'Français'}</td>
                      <td className="actions">
                        <button
                          type="button"
                          className={`btn-member-status ${
                            subscriber.isMember ? 'btn-deactivate-member' : 'btn-activate-member'
                          }`}
                          onClick={() => handleToggleMemberStatus(subscriber)}
                          disabled={memberStatusUpdatingId === subscriber._id}
                          title={
                            subscriber.isMember
                              ? 'Rendre le membre inactif'
                              : 'Rendre le membre actif'
                          }
                          aria-busy={memberStatusUpdatingId === subscriber._id}
                        >
                          {subscriber.isMember ? <FaToggleOff /> : <FaToggleOn />}
                        </button>
                        <button
                          className="btn-view"
                          onClick={() => handleViewDetails(subscriber)}
                          title="Voir détails"
                        >
                          <FaEye />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {filteredAndSortedSubscribers.length === 0 && (
              <div className="no-results">
                <p>Aucun inscrit trouvé avec ces critères</p>
              </div>
            )}
            </div>
            
            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="pagination">
                <button
                  className="pagination-btn"
                  onClick={goToPreviousPage}
                  disabled={currentPage === 1}
                >
                  <FaChevronLeft /> Précédent
                </button>
                
                <div className="pagination-info">
                  Page <strong>{currentPage}</strong> sur <strong>{totalPages}</strong>
                  {' '}(Affichage de <strong>{startIndex + 1}</strong> à <strong>{Math.min(endIndex, filteredAndSortedSubscribers.length)}</strong> sur <strong>{filteredAndSortedSubscribers.length}</strong> inscrit(s))
                </div>
                
                <button
                  className="pagination-btn"
                  onClick={goToNextPage}
                  disabled={currentPage === totalPages}
                >
                  Suivant <FaChevronRight />
                </button>
              </div>
            )}
          </>
        )}

        {/* Email Modal — portail plein écran pour éviter les contraintes du conteneur admin */}
        {showEmailModal && createPortal(
          <div className="modal-overlay modal-overlay-email" onClick={() => !emailSending && setShowEmailModal(false)}>
            <div className="modal-content modal-email" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Envoyer un email groupé</h2>
                <div className="email-modal-header-actions">
                  <button
                    type="button"
                    className="btn-preview-toggle"
                    onClick={() => setShowEmailPreview((prev) => !prev)}
                    disabled={emailSending}
                  >
                    <FaEye /> {showEmailPreview ? 'Masquer l\'aperçu' : 'Aperçu'}
                  </button>
                  <button className="btn-close" onClick={() => !emailSending && setShowEmailModal(false)} disabled={emailSending}>×</button>
                </div>
              </div>
              
              <div className="email-composer">
                <div className="recipients-info">
                  <h3>Destinataires : {selectedMembers.length} membre(s)</h3>
                  {computeBatchPlan(selectedMembers.length).batched && (
                    <div className="batch-plan-notice" role="status">
                      <strong>Envoi en lots :</strong> {computeBatchPlan(selectedMembers.length).batchCount} campagne(s) de {BULK_BATCH_SIZE} destinataires max., espacées de 24 h.
                    </div>
                  )}
                  <div className="recipients-preview">
                    {getSelectedMembersData().slice(0, 5).map(member => (
                      <span key={member._id} className="recipient-tag">
                        {member.firstName} {member.lastName}
                        <button type="button" onClick={() => handleSelectMember(member._id)}>
                          <FaTimes />
                        </button>
                      </span>
                    ))}
                    {selectedMembers.length > 5 && (
                      <span className="recipient-more">
                        +{selectedMembers.length - 5} autres
                      </span>
                    )}
                  </div>
                </div>

                <form onSubmit={handleSendEmail} className="email-form">
                  <div className="form-group">
                    <label>Expéditeur * :</label>
                    <select
                      value={emailData.fromEmail}
                      onChange={(e) => setEmailData({ ...emailData, fromEmail: e.target.value })}
                      required
                    >
                      {BULK_FROM_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Modèle d'email :</label>
                    <select 
                      value={emailData.template}
                      onChange={(e) => handleTemplateChange(e.target.value)}
                    >
                      <option value="custom">Personnalisé</option>
                      <optgroup label="Modèles prédéfinis">
                        <option value="ticketAlert">🎫 Alerte billets disponibles</option>
                        <option value="newsletter">📰 Newsletter</option>
                        <option value="reminder">⏰ Rappel mise à jour profil</option>
                        <option value="payment">💰 Rappel paiement</option>
                      </optgroup>
                      {apiTemplates.length > 0 && (
                        <optgroup label="Mes modèles">
                          {apiTemplates.map((template) => (
                            <option key={template._id} value={`api_${template._id}`}>
                              {template.name} ({template.category})
                            </option>
                          ))}
                        </optgroup>
                      )}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Sujet * :</label>
                    <input
                      type="text"
                      value={emailData.subject}
                      onChange={(e) => setEmailData({ ...emailData, subject: e.target.value })}
                      placeholder="Sujet de l'email"
                      required
                    />
                  </div>

                  <div className={`email-editor-layout ${showEmailPreview ? 'with-preview' : ''}`}>
                    <div className="form-group email-editor-group">
                      <label>Message * :</label>
                      <ReactQuill
                        theme="snow"
                        value={emailData.message}
                        onChange={(value) => setEmailData({ ...emailData, message: value })}
                        placeholder="Rédigez votre email..."
                        modules={EMAIL_QUILL_MODULES}
                        formats={EMAIL_QUILL_FORMATS}
                      />
                    </div>

                    {showEmailPreview && (
                      <div className="email-preview-pane">
                        <h3>Aperçu</h3>
                        <div className="email-preview-content">
                          <div className="email-preview-from">
                            <strong>De :</strong> {emailData.fromEmail}
                          </div>
                          <div className="email-preview-subject">
                            <strong>Sujet :</strong> {emailData.subject || '(Aucun sujet)'}
                          </div>
                          <div
                            className="email-preview-html"
                            dangerouslySetInnerHTML={{
                              __html: emailData.message || '<p><em>Aucun contenu</em></p>',
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="email-tips">
                    <h4>💡 Conseils :</h4>
                    <ul>
                      <li>Utilisez l'aperçu pour vérifier le rendu avant envoi</li>
                      <li>Choisissez l'expéditeur adapté au type de message</li>
                      <li>Au-delà de {BULK_BATCH_THRESHOLD} destinataires, l'envoi est automatiquement découpé en lots de {BULK_BATCH_SIZE} (24 h entre chaque lot)</li>
                      <li>Les membres avec email @oltrefrontiera.import ne recevront pas l'email</li>
                      <li>Évitez les sujets contenant « TEST » et les liens localhost (Infomaniak les bloque)</li>
                    </ul>
                  </div>

                  {emailSendError && (
                    <div className="email-send-error" role="alert">
                      {emailSendError}
                    </div>
                  )}

                  <div className="form-actions">
                    <button type="submit" className="btn-submit" disabled={emailSending}>
                      <FaEnvelope /> {emailSending ? 'Planification en cours…' : (
                        computeBatchPlan(selectedMembers.length).batched
                          ? `Planifier ${computeBatchPlan(selectedMembers.length).batchCount} lot(s) (${selectedMembers.length} membres)`
                          : `Envoyer à ${selectedMembers.length} membre(s)`
                      )}
                    </button>
                    <button 
                      type="button" 
                      className="btn-cancel"
                      onClick={() => setShowEmailModal(false)}
                      disabled={emailSending}
                    >
                      Annuler
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Detail Modal */}
        {showDetailModal && selectedSubscriber && (
          <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
            <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Détails de l'inscrit</h2>
                <button className="btn-close" onClick={() => setShowDetailModal(false)}>×</button>
              </div>
              
              <div className="subscriber-details">
                {/* Personal Info */}
                <section className="detail-section">
                  <h3>Informations personnelles</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>Civilité:</label>
                      <span>{selectedSubscriber.civilite}</span>
                    </div>
                    <div className="detail-item">
                      <label>Nom complet:</label>
                      <span>{selectedSubscriber.firstName} {selectedSubscriber.lastName}</span>
                    </div>
                    <div className="detail-item">
                      <label>Sexe:</label>
                      <span>{selectedSubscriber.sexe}</span>
                    </div>
                    <div className="detail-item">
                      <label>Date de naissance:</label>
                      <span>
                        {selectedSubscriber.dateOfBirth
                          ? formatBirthDateDisplayUtc(selectedSubscriber.dateOfBirth, 'fr-CH') || '—'
                          : '—'}
                      </span>
                    </div>
                    <div className="detail-item">
                      <label>Lieu de naissance:</label>
                      <span>
                        {selectedSubscriber.birthPlace?.city}
                        {selectedSubscriber.birthPlace?.canton && ` (${selectedSubscriber.birthPlace.canton})`}, 
                        {selectedSubscriber.birthPlace?.country}
                      </span>
                    </div>
                  </div>
                </section>

                {/* Contact */}
                <section className="detail-section">
                  <h3>Contact</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>Email:</label>
                      <span>{selectedSubscriber.email}</span>
                    </div>
                    <div className="detail-item">
                      <label>Téléphone:</label>
                      <span>{formatPhoneDisplay(selectedSubscriber.phone)}</span>
                    </div>
                    <div className="detail-item">
                      <label>Langue préférée:</label>
                      <span>{selectedSubscriber.preferredLanguage === 'fr' ? 'Français' : 'Italien'}</span>
                    </div>
                  </div>
                </section>

                {/* Address */}
                <section className="detail-section">
                  <h3>Adresse de résidence</h3>
                  <div className="detail-grid">
                    <div className="detail-item full-width">
                      <label>Rue:</label>
                      <span>{selectedSubscriber.address?.street} {selectedSubscriber.address?.streetNumber}</span>
                    </div>
                    <div className="detail-item">
                      <label>Code postal:</label>
                      <span>{selectedSubscriber.address?.postalCode}</span>
                    </div>
                    <div className="detail-item">
                      <label>Ville:</label>
                      <span>{selectedSubscriber.address?.city}</span>
                    </div>
                    <div className="detail-item">
                      <label>Canton:</label>
                      <span>{selectedSubscriber.address?.canton}</span>
                    </div>
                    <div className="detail-item">
                      <label>Pays:</label>
                      <span>{getDisplayCountry(selectedSubscriber)}</span>
                    </div>
                    <div className="detail-item">
                      <label>Codice fiscale:</label>
                      <span>{selectedSubscriber.codiceFiscale || '—'}</span>
                    </div>
                  </div>
                </section>

                {/* Birth Place */}
                <section className="detail-section">
                  <h3>Adresse de naissance</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>Commune de naissance:</label>
                      <span>{selectedSubscriber.birthPlace?.city || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Canton/Province/Département:</label>
                      <span>{selectedSubscriber.birthPlace?.canton || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Pays de naissance:</label>
                      <span>{selectedSubscriber.birthPlace?.country || 'N/A'}</span>
                    </div>
                  </div>
                </section>

                {/* Identity Document */}
                <section className="detail-section">
                  <h3>Document d'identité</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>Type:</label>
                      <span>{selectedSubscriber.identity?.type}</span>
                    </div>
                    <div className="detail-item">
                      <label>Numéro:</label>
                      <span>{selectedSubscriber.identity?.number}</span>
                    </div>
                    <div className="detail-item">
                      <label>Date d'expiration:</label>
                      <span>{formatDate(selectedSubscriber.identity?.expiryDate)}</span>
                    </div>
                    <div className="detail-item">
                      <label>Date d&apos;émission:</label>
                      <span>{formatDate(selectedSubscriber.identity?.issueDate)}</span>
                    </div>
                    <div className="detail-item">
                      <label>Canton/Département d&apos;émission:</label>
                      <span>{selectedSubscriber.identity?.issueCanton || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Commune/localité d&apos;émission:</label>
                      <span>{selectedSubscriber.identity?.issueCity || 'N/A'}</span>
                    </div>
                  </div>
                </section>

                {/* Juventus Fan Card */}
                {selectedSubscriber.juventusFanCard?.has && (
                  <section className="detail-section">
                    <h3>Tessera del Tifoso / Juventus Card</h3>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <label>Numéro:</label>
                        <span>{selectedSubscriber.juventusFanCard.number}</span>
                      </div>
                      <div className="detail-item">
                        <label>Date d'expiration:</label>
                        <span>{formatDate(selectedSubscriber.juventusFanCard.expiryDate)}</span>
                      </div>
                      <div className="detail-item">
                        <label>PIN Webticket:</label>
                        <span>{selectedSubscriber.juventusFanCard.webticketPIN || 'N/A'}</span>
                      </div>
                    </div>
                  </section>
                )}

                {/* Subscription */}
                {selectedSubscriber.subscription?.has && (
                  <section className="detail-section">
                    <h3>Abonnement</h3>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <label>Tribune:</label>
                        <span>{selectedSubscriber.subscription.tribune}</span>
                      </div>
                      <div className="detail-item">
                        <label>Settore:</label>
                        <span>{selectedSubscriber.subscription.settore}</span>
                      </div>
                      <div className="detail-item">
                        <label>Fila:</label>
                        <span>{selectedSubscriber.subscription.fila}</span>
                      </div>
                      <div className="detail-item">
                        <label>Posto:</label>
                        <span>{selectedSubscriber.subscription.posto}</span>
                      </div>
                    </div>
                  </section>
                )}

                {/* Black & White Card */}
                {selectedSubscriber.blackWhiteCard?.has && (
                  <section className="detail-section">
                    <h3>Carte Black & White / J1897</h3>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <label>Numéro:</label>
                        <span>{selectedSubscriber.blackWhiteCard.number}</span>
                      </div>
                      <div className="detail-item">
                        <label>Date d'expiration:</label>
                        <span>{formatDate(selectedSubscriber.blackWhiteCard.expiryDate)}</span>
                      </div>
                    </div>
                  </section>
                )}

                {/* Payment Info */}
                <section className="detail-section">
                  <h3>Paiement</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>Méthode:</label>
                      <span>{selectedSubscriber.payment?.method || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Type:</label>
                      <span>{selectedSubscriber.payment?.type || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Montant:</label>
                      <span>
                        {selectedSubscriber.payment?.amount ? 
                          `${selectedSubscriber.payment.amount} ${selectedSubscriber.payment.currency}` : 
                          'N/A'}
                      </span>
                    </div>
                    <div className="detail-item">
                      <label>Statut:</label>
                      <span className={`badge badge-${
                        selectedSubscriber.payment?.status === 'completed' ? 'success' :
                        selectedSubscriber.payment?.status === 'pending' ? 'warning' : 'danger'
                      }`}>
                        {selectedSubscriber.payment?.status === 'completed' ? 'Payé' :
                         selectedSubscriber.payment?.status === 'pending' ? 'En attente' :
                         selectedSubscriber.payment?.status === 'failed' ? 'Échoué' : 'N/A'}
                      </span>
                    </div>
                    <div className="detail-item">
                      <label>Date:</label>
                      <span>{formatDate(selectedSubscriber.payment?.date)}</span>
                    </div>
                  </div>
                </section>

                {/* Membership Status */}
                <section className="detail-section">
                  <h3>Statut membre</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>Membre actif:</label>
                      <span className={`badge ${selectedSubscriber.isMember ? 'badge-success' : 'badge-warning'}`}>
                        {selectedSubscriber.isMember ? 'Oui' : 'Non'}
                      </span>
                    </div>
                    <div className="detail-item full-width">
                      <label>Saisons actives:</label>
                      <span>{formatActiveSeasonsCell(selectedSubscriber)}</span>
                    </div>
                    {selectedSubscriber.membershipDate && (
                      <div className="detail-item">
                        <label>Date d'adhésion:</label>
                        <span>{formatDate(selectedSubscriber.membershipDate)}</span>
                      </div>
                    )}
                    <div className="detail-item">
                      <label>Crédit billetterie:</label>
                      <span>{selectedSubscriber.ticketCredit} CHF</span>
                    </div>
                    <div className="detail-item">
                      <label>Compte activé:</label>
                      <span>{selectedSubscriber.accountActivated ? 'Oui' : 'Non'}</span>
                    </div>
                  </div>
                </section>

                {/* Consents */}
                <section className="detail-section">
                  <h3>Consentements</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>Marketing:</label>
                      <span>{selectedSubscriber.consents?.marketing ? '✓ Oui' : '✗ Non'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Profilage:</label>
                      <span>{selectedSubscriber.consents?.profiling ? '✓ Oui' : '✗ Non'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Droit à l'image:</label>
                      <span>{selectedSubscriber.consents?.imageRights ? '✓ Oui' : '✗ Non'}</span>
                    </div>
                    <div className="detail-item">
                      <label>CGV acceptées:</label>
                      <span>{selectedSubscriber.consents?.termsAccepted ? '✓ Oui' : '✗ Non'}</span>
                    </div>
                  </div>
                </section>

                {/* Parent Info (if minor) */}
                {selectedSubscriber.isMinor && selectedSubscriber.parentInfo && (
                  <section className="detail-section">
                    <h3>Informations parent (mineur)</h3>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <label>Nom parent:</label>
                        <span>{selectedSubscriber.parentInfo.firstName} {selectedSubscriber.parentInfo.lastName}</span>
                      </div>
                      <div className="detail-item">
                        <label>Email parent:</label>
                        <span>{selectedSubscriber.parentInfo.email}</span>
                      </div>
                      <div className="detail-item">
                        <label>Date de naissance parent:</label>
                        <span>
                          {selectedSubscriber.parentInfo?.dateOfBirth
                            ? formatBirthDateDisplayUtc(selectedSubscriber.parentInfo.dateOfBirth, 'fr-CH') ||
                              '—'
                            : '—'}
                        </span>
                      </div>
                    </div>
                  </section>
                )}

                {/* Other info */}
                <section className="detail-section">
                  <h3>Informations supplémentaires</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>Membre d'un autre fan club:</label>
                      <span>{selectedSubscriber.otherFanClub?.isMember ? 'Oui' : 'Non'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Date d'inscription:</label>
                      <span>{formatDate(selectedSubscriber.createdAt)}</span>
                    </div>
                    {selectedSubscriber.lastLogin && (
                      <div className="detail-item">
                        <label>Dernière connexion:</label>
                        <span>{formatDate(selectedSubscriber.lastLogin)}</span>
                      </div>
                    )}
                  </div>
                </section>
              </div>

              <div className="modal-footer">
                <button 
                  className="btn-cancel"
                  onClick={() => setShowDetailModal(false)}
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Parent Details Modal */}
        {showParentModal && selectedParent && (
          <div className="modal-overlay" onClick={() => setShowParentModal(false)}>
            <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Informations du parent {selectedParent.isRegisteredUser && <span className="parent-badge-inline" title="Parent enregistré dans le système">✓ Enregistré</span>}</h2>
                <button className="btn-close" onClick={() => setShowParentModal(false)}>×</button>
              </div>
              
              <div className="subscriber-details">
                {selectedParent.isRegisteredUser ? (
                  <>
                    {/* Personal Info */}
                    <section className="detail-section">
                      <h3>Informations personnelles</h3>
                      <div className="detail-grid">
                        <div className="detail-item">
                          <label>Civilité:</label>
                          <span>{selectedParent.civilite}</span>
                        </div>
                        <div className="detail-item">
                          <label>Nom parent:</label>
                          <span>{selectedParent.lastName || '—'}</span>
                        </div>
                        <div className="detail-item">
                          <label>Prénom parent:</label>
                          <span>{selectedParent.firstName || '—'}</span>
                        </div>
                        <div className="detail-item">
                          <label>Sexe Parent:</label>
                          <span>
                            {selectedParent.sexe === 'Homme' ? 'Male' : 
                             selectedParent.sexe === 'Femme' ? 'Female' : 
                             selectedParent.sexe || '—'}
                          </span>
                        </div>
                        <div className="detail-item">
                          <label>Pays de naissance parent:</label>
                          <span>{selectedParent.birthPlace?.country || '—'}</span>
                        </div>
                        <div className="detail-item">
                          <label>Canton/Province/Département de naissance parent:</label>
                          <span>{selectedParent.birthPlace?.canton || '—'}</span>
                        </div>
                        <div className="detail-item">
                          <label>Ville naissance parent:</label>
                          <span>{selectedParent.birthPlace?.city || '—'}</span>
                        </div>
                        <div className="detail-item">
                          <label>Date naissance parent:</label>
                          <span>
                            {selectedParent.dateOfBirth
                              ? formatBirthDateDisplayUtc(selectedParent.dateOfBirth, 'fr-CH') || '—'
                              : '—'}
                          </span>
                        </div>
                      </div>
                    </section>

                    {/* Contact */}
                    <section className="detail-section">
                      <h3>Contact</h3>
                      <div className="detail-grid">
                        <div className="detail-item">
                          <label>Email parent:</label>
                          <span>{selectedParent.email || '—'}</span>
                        </div>
                        <div className="detail-item">
                          <label>Téléphone:</label>
                          <span>{formatPhoneDisplay(selectedParent.phone)}</span>
                        </div>
                        <div className="detail-item">
                          <label>Langue préférée:</label>
                          <span>{selectedParent.preferredLanguage === 'fr' ? 'Français' : selectedParent.preferredLanguage === 'it' ? 'Italien' : '—'}</span>
                        </div>
                      </div>
                    </section>

                    {/* Address */}
                    <section className="detail-section">
                      <h3>Adresse de résidence</h3>
                      <div className="detail-grid">
                        <div className="detail-item full-width">
                          <label>Rue:</label>
                          <span>{selectedParent.address?.street} {selectedParent.address?.streetNumber}</span>
                        </div>
                        <div className="detail-item">
                          <label>Code postal:</label>
                          <span>{selectedParent.address?.postalCode}</span>
                        </div>
                        <div className="detail-item">
                          <label>Ville:</label>
                          <span>{selectedParent.address?.city}</span>
                        </div>
                        <div className="detail-item">
                          <label>Canton:</label>
                          <span>{selectedParent.address?.canton}</span>
                        </div>
                        <div className="detail-item">
                          <label>Pays:</label>
                          <span>{selectedParent.address?.country}</span>
                        </div>
                      </div>
                    </section>

                    {/* Membership Status */}
                    <section className="detail-section">
                      <h3>Statut membre</h3>
                      <div className="detail-grid">
                        <div className="detail-item">
                          <label>Membre actif:</label>
                          <span className={`badge ${selectedParent.isMember ? 'badge-success' : 'badge-warning'}`}>
                            {selectedParent.isMember ? 'Oui' : 'Non'}
                          </span>
                        </div>
                        {selectedParent.membershipDate && (
                          <div className="detail-item">
                            <label>Date d'adhésion:</label>
                            <span>{formatDate(selectedParent.membershipDate)}</span>
                          </div>
                        )}
                        <div className="detail-item">
                          <label>Date d'inscription:</label>
                          <span>{formatDate(selectedParent.createdAt)}</span>
                        </div>
                      </div>
                    </section>
                  </>
                ) : (
                  <>
                    {/* Embedded Parent Info (not registered user) */}
                    <section className="detail-section">
                      <h3>Informations du parent</h3>
                      <div className="detail-grid">
                        <div className="detail-item">
                          <label>Nom parent:</label>
                          <span>{selectedParent.lastName || '—'}</span>
                        </div>
                        <div className="detail-item">
                          <label>Prénom parent:</label>
                          <span>{selectedParent.firstName || '—'}</span>
                        </div>
                        <div className="detail-item">
                          <label>Sexe Parent:</label>
                          <span>
                            {selectedParent.sexe === 'Homme' ? 'Male' : 
                             selectedParent.sexe === 'Femme' ? 'Female' : 
                             selectedParent.sexe || '—'}
                          </span>
                        </div>
                        <div className="detail-item">
                          <label>Pays de naissance parent:</label>
                          <span>{selectedParent.birthCountry || '—'}</span>
                        </div>
                        <div className="detail-item">
                          <label>Canton/Province/Département de naissance parent:</label>
                          <span>{selectedParent.birthCanton || '—'}</span>
                        </div>
                        <div className="detail-item">
                          <label>Ville naissance parent:</label>
                          <span>{selectedParent.birthCity || '—'}</span>
                        </div>
                        <div className="detail-item">
                          <label>Date naissance parent:</label>
                          <span>
                            {selectedParent.dateOfBirth
                              ? formatBirthDateDisplayUtc(selectedParent.dateOfBirth, 'fr-CH') || '—'
                              : '—'}
                          </span>
                        </div>
                        <div className="detail-item">
                          <label>Email parent:</label>
                          <span>{selectedParent.email || '—'}</span>
                        </div>
                      </div>
                    </section>
                    <div className="info-box">
                      <p><strong>Note:</strong> Ce parent n'est pas encore enregistré comme membre dans le système. Les informations affichées proviennent du formulaire d'inscription du mineur.</p>
                    </div>
                  </>
                )}
              </div>

              <div className="modal-footer">
                <button 
                  className="btn-cancel"
                  onClick={() => setShowParentModal(false)}
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSubscribers;

